import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n/index";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useState(i18n.language === "zh-TW" ? "zh-TW" : "en");

  const navLinks = [
    { label: t("nav_home"), href: "#home" },
    { label: t("nav_accounting"), href: "#accounting-services" },
    { label: t("nav_taxation"), href: "#taxation-services" },
    { label: t("nav_mpf"), href: "#mpf-services" },
    { label: t("nav_about"), href: "#about" },
    { label: t("nav_contact"), href: "#contact" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: "smooth" });
  };

  const toggleLang = () => {
    const next = lang === "en" ? "zh-TW" : "en";
    setLang(next);
    i18n.changeLanguage(next);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-sm py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); handleNav("#home"); }}
          className="flex items-center gap-3 cursor-pointer"
        >
          <img
            src="https://static.readdy.ai/image/4f9f6a64cb84e58f95d0a42c8c0ffa57/f3822a50bf426e713c09d7fc92b2de01.jpeg"
            alt="Nice Accounting Logo"
            className="h-10 w-auto object-contain"
          />
          <span className={`font-semibold text-lg tracking-wide hidden sm:block ${scrolled ? "text-[#1C2331]" : "text-white"}`}>
            Nice Accounting
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? "text-[#4A4A4A] hover:text-[#D4927A]" : "text-white/90 hover:text-white"
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Right controls */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Language toggle */}
          <button
            onClick={toggleLang}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
              scrolled
                ? "border-[#D4927A] text-[#D4927A] hover:bg-[#D4927A] hover:text-white"
                : "border-white/60 text-white/80 hover:border-white hover:text-white"
            }`}
          >
            <i className="ri-translate-2 text-sm" />
            {lang === "en" ? "中文" : "EN"}
          </button>

          {/* CTA */}
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNav("#contact"); }}
            className="flex items-center gap-2 bg-[#D4927A] hover:bg-[#C07F6A] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("nav_cta")}
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        {/* Mobile: lang toggle + hamburger */}
        <div className="lg:hidden flex items-center gap-2">
          <button
            onClick={toggleLang}
            className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
              scrolled
                ? "border-[#D4927A] text-[#D4927A]"
                : "border-white/60 text-white/80"
            }`}
          >
            {lang === "en" ? "中文" : "EN"}
          </button>
          <button
            className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors ${scrolled ? "text-[#1C2331]" : "text-white"}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg px-6 py-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
              className="block py-3 text-sm font-medium text-[#1C2331] hover:text-[#D4927A] border-b border-gray-50 cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNav("#contact"); }}
            className="mt-3 flex items-center justify-center gap-2 bg-[#D4927A] text-white text-sm font-semibold px-5 py-3 rounded-full cursor-pointer whitespace-nowrap"
          >
            {t("nav_cta")} <i className="ri-arrow-right-line" />
          </a>
        </div>
      )}
    </header>
  );
}
